import * as React from 'react';
import { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { api } from "../../utils/axios";
import { useAppSelector } from "../../store/hooks";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./upload.css";
import { Link } from "react-router-dom";

interface UploadedFile {
  key: string;
  filename: string;
  uploaded_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const allowedExtensions = ['csv', 'xlsx', 'xls'];

const isAllowedFileType = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext && allowedExtensions.includes(ext);
};

const UploadPage: React.FC = () => {
  const clientId = useAppSelector((s) => s.auth.clientID);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [existing, setExisting] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const FILES_PER_PAGE = 5;
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(existing.length / FILES_PER_PAGE);
  const paginatedFiles = existing.slice((page - 1) * FILES_PER_PAGE, page * FILES_PER_PAGE);

  const fetchExisting = useCallback(async () => {
    try {
      const resp = await api.get<{ files: UploadedFile[] }>("/list_uploads", {
        params: { client_id: clientId },
      });
      setExisting(resp.data.files);
    } catch (e) {
      console.error("fetchExisting error:", e);
    }
  }, [clientId]);

  useEffect(() => {
    fetchExisting();
  }, [fetchExisting]);

  useEffect(() => {
    if (!uploadId) return;
    const es = new EventSource(`${API_URL}/upload_status/${uploadId}`, { withCredentials: true });

    es.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.status === "s3_done") {
        toast.success(`🎉 ${msg.key} uploaded to S3`);
      }
      if (msg.status === "all_done") {
        toast.info("✅ All files finished uploading");
        fetchExisting();
        es.close();
      }
    };

    es.onerror = (err) => {
      console.error("SSE error", err);
      es.close();
    };

    return () => es.close();
  }, [uploadId, fetchExisting]);

  const onDrop = useCallback((accepted: File[], rejected: File[] = []) => {
    const validFiles = accepted.filter(isAllowedFileType);
    const invalidFiles = accepted.filter((f) => !isAllowedFileType(f));

    if (invalidFiles.length > 0) {
      toast.error("Only CSV, XLSX, or XLS files are allowed.");
    }

    setFiles((curr) => {
      const seen = new Set(curr.map((f) => f.name + f.size));
      return [...curr, ...validFiles.filter((f) => !seen.has(f.name + f.size))];
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
  });

  const removeFile = (f: File) =>
      setFiles((curr) => curr.filter((x) => x !== f));

  const uploadFiles = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append("client_id", clientId);
    files.forEach((f) => form.append("files", f));

    try {
      const resp = await api.post("/upload_files", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          setProgress(Math.round((evt.loaded / evt.total!) * 100));
        },
      });
      toast.info("⏳ Uploading files.... ");
      setUploadId(resp.data.upload_id);
      fetchExisting();
      setFiles([]);
    } catch (e: any) {
      console.error("uploadFiles error:", e);
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (key: string) => {
    try {
      const filename = key.split("/").pop();
      const resp = await api.get("/get_file", {
        params: { client_id: clientId, key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteFile = async (key: string) => {
    try {
      await api.delete("/delete_file", {
        params: { client_id: clientId, key },
      });
      fetchExisting();
      toast.info("Deleted " + key);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
      <div className="upload-container-wide">
        <ToastContainer position="top-right" />
        <div className="upload-container">
          <div className="upload-content-box">

            {/* Upload Section */}
            <div className="upload-section">
              <div className="page-header">
                <h2 className="upload-title">Upload Roster Files</h2>
              </div>

              

              <div {...getRootProps()} className={`drop-zone ${isDragActive ? "drop-zone--active" : ""}`}>
                <input {...getInputProps()} />
                <p>
                  {isDragActive
                      ? "Drop your CSV/XLSX/XLS files here…"
                      : "Drag & drop or click to upload CSV, XLSX, or XLS files"}
                </p>
              </div>

              {files.length > 0 && (
                  <div className="file-list">
                    {files.map((f, i) => (
                        <div className="file-item" key={i}>
                          <span>{f.name}</span>
                          <button className="remove-button" onClick={() => removeFile(f)}>×</button>
                        </div>
                    ))}
                  </div>
              )}

              {uploading && (
                  <div className="progress-container">
                    <progress value={progress} max={100} />
                    <span>{progress}%</span>
                  </div>
              )}

              <button
                  className="upload-button"
                  onClick={uploadFiles}
                  disabled={uploading || files.length === 0}
              >
                {uploading ? "Uploading…" : "Upload Files"}
              </button>
            </div>

            {/* Uploaded Files Section */}
            <div className="upload-table-section">
              <h3>Your Uploaded Files</h3>
              <table className="uploaded-table">
                <thead>
                <tr>
                  <th>Uploaded At</th>
                  <th>Filename</th>
                </tr>
                </thead>
                <tbody>
                {paginatedFiles.map((f) => (
                    <tr key={f.key}>
                      <td>{f.uploaded_at}</td>
                      <td>{f.filename}</td>
                    </tr>
                ))}
                {existing.length === 0 && (
                    <tr>
                      <td colSpan={2}>No files uploaded yet.</td>
                    </tr>
                )}
                </tbody>
              </table>

              {existing.length > FILES_PER_PAGE && (
                  <div className="upload-pagination">
                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
                    <span style={{ margin: "0 1rem" }}>
                Page {page} of {totalPages}
              </span>
                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                  </div>
              )}
            </div>

          </div>
        </div>
      </div>
  );
};

export default UploadPage;
